import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const sections = [
  {
    title: "Innovative Designs",
    text: `We create modern, innovative website designs that not only look visually stunning but also enhance user engagement. Our web solutions are crafted to reflect your brand identity while providing seamless navigation and experience for your audience.`,
    img: "../../web1.png",
  },
  {
    title: "Responsive Websites",
    text: `Our responsive websites ensure perfect display on all devices — desktops, tablets, and smartphones. By focusing on usability and mobile-first design, we provide a smooth, intuitive, and accessible browsing experience for every visitor.`,
    img: "../../web2.png",
    reverse: true,
  },
  {
    title: "Dynamic Functionality",
    text: `From interactive UI elements to dynamic content and real-time updates, we develop websites that are both functional and engaging. We implement modern technologies to make your website adaptable and future-proof for your users.`,
    img: "../../web3.png",
  },
  {
    title: "Custom Web Solutions",
    text: `We provide fully customized web solutions tailored to your business goals. Our team ensures easy management, scalability, and optimization so your website grows along with your brand, making it unique, efficient, and productive.`,
    img: "../../web4.png",
    reverse: true,
  },
];

const ScrollSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    viewport={{ once: false, amount: 0.3 }}
  >
    {children}
  </motion.div>
);

export default function WebDevelopmentTailwind() {
  const handleScroll = () => {
    document.getElementById("web-sections").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="font-poppins text-gray-900 overflow-x-hidden scroll-smooth relative">
      {/* Background */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url('../../webde.jpg')` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Hero Section */}
      <ScrollSection>
        <section className="relative h-[85vh] sm:h-screen flex flex-col items-center justify-center text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-7xl font-extrabold uppercase tracking-wider mb-5 text-white drop-shadow-lg"
          >
            Web Development
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-base sm:text-lg md:text-xl max-w-xl sm:max-w-2xl text-gray-300 leading-relaxed px-2"
          >
            Why are we the best Web Development Company in Indore?
          </motion.p>

          {/* Scroll Arrow */}
          <motion.div
            className="absolute bottom-10 sm:bottom-20 left-1/2 -translate-x-1/2 cursor-pointer animate-bounce text-red-600"
            onClick={handleScroll}
            whileHover={{ scale: 1.2 }}
          >
            <ChevronDown size={38} className="sm:w-10 sm:h-10" />
          </motion.div>
        </section>
      </ScrollSection>

      {/* Content Sections */}
      <div id="web-sections" className="max-w-6xl mx-auto py-16 sm:py-24 px-4 sm:px-6 md:px-10 space-y-20 sm:space-y-24 relative z-10">
        {sections.map(({ title, text, img, reverse }, idx) => (
          <ScrollSection key={idx}>
            <div
              className={`flex flex-col md:flex-row items-center gap-10 sm:gap-12 md:gap-16 ${
                reverse ? "md:flex-row-reverse" : ""
              }`}
            >
              {/* Text Box */}
              <motion.div
                className="flex-1 bg-white/90 backdrop-blur-xl p-6 sm:p-10 md:p-12 rounded-2xl shadow-[0_10px_25px_rgba(209,17,17,0.2)] hover:shadow-[0_14px_35px_rgba(209,17,17,0.35)] transition-all duration-300"
                whileHover={{ y: -5 }}
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 relative after:block after:w-16 sm:after:w-20 after:h-1 after:bg-red-600 after:rounded mt-2">
                  {title}
                </h2>
                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700">{text}</p>
              </motion.div>

              {/* Image */}
              <motion.div
                className="flex-1 text-center"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              >
                <img
                  src={img}
                  alt={title}
                  className="w-full max-w-xs sm:max-w-sm md:max-w-md rounded-2xl shadow-[0_12px_30px_rgba(209,17,17,0.2)]"
                />
              </motion.div>
            </div>
          </ScrollSection>
        ))}
      </div>

      <style>{`
        @keyframes bounce {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        .animate-bounce {
          animation: bounce 1.8s infinite;
        }
      `}</style>
    </div>
  );
}